import type { AustralianState } from "@/lib/availability";

// Real Google reviews collected from Supreme Turf's Google Business profiles
// across each location. Only the reviewer name, star rating and review text are
// stored — lightly cleaned for spelling/grammar and trimmed for length. No
// customer contact details or private data are kept.
export interface LocationReview {
  name: string;
  rating: number;
  text: string;
}

export const LOCATION_REVIEWS: Record<AustralianState, LocationReview[]> = {
  QLD: [
    {
      name: "Brendan Strong",
      rating: 5,
      text: "I own an artificial grass installation company and can't recommend Supreme Turf highly enough! From the quality products to fantastic customer service, the guys appreciate timelines and always go above and beyond. I wouldn't go anywhere else.",
    },
    {
      name: "Subaita Mobasshar",
      rating: 5,
      text: "Really happy with Supreme Turf. The grass looks natural and needs very little maintenance.",
    },
    {
      name: "Anoop Andrews",
      rating: 5,
      text: "Awesome product, very good customer service and reasonable price. Definitely recommend this place.",
    },
    {
      name: "Ravi K.",
      rating: 5,
      text: "Great experience with Supreme Turf. Their products are affordable without compromising on quality — the artificial grass looks premium and natural, and the finishing is excellent. Very competitive pricing. Highly recommended.",
    },
    {
      name: "Daniel P.",
      rating: 5,
      text: "Top-notch artificial grass. Good price, clean work and a friendly team.",
    },
  ],
  NSW: [
    {
      name: "Jignesh Acharya",
      rating: 5,
      text: "Great experience trading with the team. Great price with the best quality. I'd recommend them for any kind of feature wall panels or artificial turf. Overall 5/5 and highly recommend to anyone looking for a reliable supplier.",
    },
    {
      name: "Sterlin Pty Ltd",
      rating: 5,
      text: "We have been customers for a long time. Amazing service and the best quality artificial grass and WPC cladding. Recently we did a big decking project and were absolutely happy with the decking material and price. Highly recommended.",
    },
    {
      name: "Kamal Kaur",
      rating: 5,
      text: "Supreme Turf are the best suppliers of artificial grass, WPC decking, wall cladding and pebbles in Sydney, and the quality of their products is very good. Their team is very friendly — Arsh helped me a lot to choose the right product for my room.",
    },
    {
      name: "Shivani Monpara",
      rating: 5,
      text: "Quality WPC cladding and artificial grass with excellent service and same-day delivery! My garden looks amazing with the artificial turf, and the best part is I don't have to maintain it.",
    },
    {
      name: "Pravesh Shah",
      rating: 5,
      text: "An absolute delight to deal with! Staff are helpful, with plenty of stock to choose from at very reasonable prices. Highly recommended.",
    },
    {
      name: "Brisbane Girl",
      rating: 5,
      text: "Love it. The colour of the artificial lawn looks so real and thick — we used the yellow-green. They cut the pieces to size for us so they were easy to install.",
    },
    {
      name: "Raju T N",
      rating: 5,
      text: "Highly recommended. The boys working there, Arjun and Arsh, are super good and always look after me.",
    },
    {
      name: "Arun Sreenivas",
      rating: 5,
      text: "Exactly as advertised, and good professional advice on what to choose as well.",
    },
    {
      name: "Kuljeet Saini",
      rating: 5,
      text: "Best product and great service. Very helpful with loading too.",
    },
    {
      name: "Meherin Shipa",
      rating: 5,
      text: "Good communication and quality product. Happy with the overall service.",
    },
  ],
  VIC: [
    {
      name: "Dilip Rajput",
      rating: 5,
      text: "Bought artificial grass from them 7 years ago and it's still in brand-new condition. Today we bought again for a different property — same great quality, and amazingly they haven't increased the price.",
    },
    {
      name: "Ranjeet Singh Bal",
      rating: 5,
      text: "I couldn't be happier with my experience! The team was professional, knowledgeable and incredibly helpful. The quality of the decking, cladding and artificial grass was outstanding — durable, modern and exactly what I was looking for. Delivery was prompt and everything arrived in perfect condition. Highly recommend!",
    },
    {
      name: "hannah ikaheimonen",
      rating: 5,
      text: "Amazing service, great staff — super friendly and helpful — and great turf!",
    },
    {
      name: "pinky patel",
      rating: 5,
      text: "I checked 3 different places but didn't find what I was hoping for. Finally got exactly what I wanted here. Very happy with the lush green 40mm at an unbeatable price.",
    },
    {
      name: "Meherin Shipa",
      rating: 5,
      text: "Fast service and excellent turf quality. Would definitely recommend.",
    },
    {
      name: "Ray",
      rating: 5,
      text: "Great price, good quality and good service!",
    },
  ],
  SA: [
    {
      name: "Jaydev Kibadia",
      rating: 5,
      text: "The product is very genuine and the best quality in the market. The pricing is great compared to others around. It's a one-stop destination for all your landscaping needs.",
    },
    {
      name: "Swapnil Patel",
      rating: 5,
      text: "Huge selection of cladding and great customer service by JD and the team. Highly recommend Supreme Turf.",
    },
    {
      name: "maitry viradiya",
      rating: 5,
      text: "Excellent quality and super soft grass. The natural-colour grass they stock is especially rare to find.",
    },
    {
      name: "Hossein Mahdavan",
      rating: 5,
      text: "Good customer service. The boys are very experienced and happy to help with all sorts of needs. Highly approachable and great quality.",
    },
    {
      name: "Dhruvkumar Patel",
      rating: 5,
      text: "Best quality turf and cladding — really happy with the product. Must recommend.",
    },
  ],
};

/** All reviews across every location, used when no location is selected. */
export const ALL_REVIEWS: LocationReview[] = Object.values(LOCATION_REVIEWS).flat();

/**
 * Returns reviews for a location. If a location has fewer than `min` reviews,
 * it's topped up with high-rated reviews from other locations so the section
 * never looks sparse. When no state is selected, a blended list is returned.
 */
export function getReviewsForState(
  state: AustralianState | null,
  min = 6,
): LocationReview[] {
  if (!state) return ALL_REVIEWS;
  const own = LOCATION_REVIEWS[state] ?? [];
  if (own.length >= min) return own;
  const seen = new Set(own.map((r) => r.name + r.text.slice(0, 20)));
  const extras = ALL_REVIEWS.filter(
    (r) => r.rating === 5 && !seen.has(r.name + r.text.slice(0, 20)),
  );
  return [...own, ...extras].slice(0, Math.max(min, own.length));
}

/** Aggregate rating + count for a set of reviews. */
export function reviewStats(reviews: LocationReview[]): {
  average: number;
  count: number;
} {
  if (reviews.length === 0) return { average: 5, count: 0 };
  const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
  return {
    average: Math.round((sum / reviews.length) * 10) / 10,
    count: reviews.length,
  };
}
